/**
 * Modal — Radix `Dialog` wrapper with the radical scrim + sharp-corner shell.
 *
 *   <Modal open={open} onOpenChange={setOpen}>
 *     <DialogCrown
 *       tone="warn"
 *       eyebrow="Pozor — možný duplikát"
 *       eyebrowMeta={<>3 podobné záznamy</>}
 *       title={<>Možná chceš použít <em>existujícího</em> zákazníka.</>}
 *       sub="Našli jsme záznamy, které vypadají podobně…"
 *     />
 *     <Modal.Body>…</Modal.Body>
 *     <Modal.Foot meta="Esc · zavřít" actions={…} />
 *   </Modal>
 *
 * Source: .agents/design/project/artboard-radical-duplicate-modal.jsx:81-247
 */

import * as Dialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import {
  AnimatePresence,
  motion,
  useDragControls,
  useReducedMotion,
  type PanInfo,
} from 'framer-motion';
import { useBreakpoint } from '@/lib/useBreakpoint';

export type ModalTone = 'neutral' | 'warn' | 'danger';

interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  /** Max width of the dialog. Defaults to `760px`. */
  maxWidth?: number;
  className?: string;
  /** Optional accessible label when no `DialogCrown` title is rendered. */
  ariaLabel?: string;
}

export function Modal({
  open,
  onOpenChange,
  children,
  maxWidth = 760,
  className = '',
  ariaLabel,
}: ModalProps) {
  const reduce = useReducedMotion();
  // Phase 14.4: below `sm` every dialog renders as a bottom sheet (handoff
  // `.modal` — full width, bottom-anchored, 92% max height, slide-up,
  // drag-down to dismiss). Tablet + desktop keep the centered dialog.
  const { isPhone } = useBreakpoint();
  // Drag-to-dismiss starts ONLY from the grab handle (dragListener={false} +
  // dragControls). A whole-sheet drag listener would set `touch-action:
  // pan-x` on the sheet and kill vertical touch scrolling inside Modal.Body,
  // trapping users in long form dialogs.
  const sheetDragControls = useDragControls();

  function onSheetDragEnd(_e: unknown, info: PanInfo) {
    if (info.offset.y > 80 || info.velocity.y > 500) onOpenChange(false);
  }

  // Phase 12.2: scrim + content are driven by framer-motion instead of
  // tw-animate-css `data-[state=*]:animate-*` classes so the exit animation
  // actually plays (Radix unmounts immediately by default; AnimatePresence
  // owns the unmount tween). `forceMount` is required so AnimatePresence
  // observes the mount/unmount lifecycle, not Radix's internal state.
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open ? (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-40 bg-[rgba(14,16,20,0.55)] backdrop-blur-md dark:bg-[rgba(0,0,0,0.65)]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: reduce ? 0 : 0.18,
                  ease: [0.2, 0, 0.13, 1],
                }}
              />
            </Dialog.Overlay>
            <Dialog.Content
              asChild
              forceMount
              aria-label={ariaLabel}
            >
              {isPhone ? (
                <motion.div
                  data-modal-mode="sheet"
                  className={
                    'fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] w-full flex-col border-t border-line bg-paper shadow-[0_-24px_64px_rgba(0,0,0,0.30)] focus:outline-none ' +
                    className
                  }
                  initial={reduce ? { opacity: 0 } : { y: '100%' }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={reduce ? { opacity: 0 } : { y: '100%' }}
                  transition={{
                    duration: reduce ? 0 : 0.24,
                    ease: [0.2, 0, 0.13, 1],
                  }}
                  drag={reduce ? false : 'y'}
                  dragControls={sheetDragControls}
                  dragListener={false}
                  dragConstraints={{ top: 0, bottom: 0 }}
                  dragElastic={{ top: 0, bottom: 0.6 }}
                  onDragEnd={onSheetDragEnd}
                >
                  {!reduce && (
                    <div
                      aria-hidden
                      data-sheet-handle
                      onPointerDown={(e) => sheetDragControls.start(e)}
                      className="flex shrink-0 cursor-grab touch-none justify-center pb-1.5 pt-2.5 active:cursor-grabbing"
                    >
                      <span className="h-[3px] w-10 bg-line-2" />
                    </div>
                  )}
                  {children}
                </motion.div>
              ) : (
                <motion.div
                  data-modal-mode="dialog"
                  className={
                    'fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-[calc(100vw-80px)] flex-col border border-line bg-paper shadow-[0_24px_64px_rgba(0,0,0,0.30),0_1px_0_rgba(0,0,0,0.05)] focus:outline-none ' +
                    className
                  }
                  style={{ maxWidth }}
                  initial={reduce ? { x: '-50%', y: '-50%', opacity: 0 } : {
                    x: '-50%',
                    y: 'calc(-50% + 8px)',
                    opacity: 0,
                    scale: 0.97,
                  }}
                  animate={{ x: '-50%', y: '-50%', opacity: 1, scale: 1 }}
                  exit={reduce ? { x: '-50%', y: '-50%', opacity: 0 } : {
                    x: '-50%',
                    y: 'calc(-50% + 4px)',
                    opacity: 0,
                    scale: 0.98,
                  }}
                  transition={{
                    duration: reduce ? 0 : 0.22,
                    ease: [0.2, 0, 0.13, 1],
                  }}
                >
                  {children}
                </motion.div>
              )}
            </Dialog.Content>
          </Dialog.Portal>
        ) : null}
      </AnimatePresence>
    </Dialog.Root>
  );
}

interface ModalBodyProps {
  children: ReactNode;
  className?: string;
}

function Body({ children, className = '' }: ModalBodyProps) {
  return (
    <div className={'flex-1 overflow-y-auto ' + className}>{children}</div>
  );
}
Modal.Body = Body;

interface ModalSectionProps {
  /** Mono uppercase eyebrow with optional inline count chip. */
  heading?: ReactNode;
  /** Right-aligned hint (e.g. "seřazeno podle síly shody"). */
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}

function Section({ heading, hint, children, className = '' }: ModalSectionProps) {
  return (
    <section
      className={
        'border-b border-line px-8 pb-[18px] pt-[22px] last:border-b-0 max-sm:px-5 ' +
        className
      }
    >
      {(heading || hint) && (
        <div className="mb-3.5 flex items-baseline justify-between font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          {heading && <span>{heading}</span>}
          {hint && (
            <span className="text-[11px] normal-case tracking-normal">
              {hint}
            </span>
          )}
        </div>
      )}
      {children}
    </section>
  );
}
Modal.Section = Section;

interface ModalFootProps {
  /** Mono meta text — keyboard hints, etc. */
  meta?: ReactNode;
  /** Right-side action cluster — typically `<Button>` pair. */
  actions: ReactNode;
  className?: string;
}

function Foot({ meta, actions, className = '' }: ModalFootProps) {
  return (
    <footer
      className={
        'flex items-center justify-between gap-4 border-t border-line bg-paper px-8 py-[18px] max-sm:px-5 max-sm:py-3.5 ' +
        className
      }
    >
      <div className="font-mono text-[11px] text-ink-3 [&_b]:font-medium [&_b]:text-ink">
        {meta}
      </div>
      <div className="flex gap-2.5">{actions}</div>
    </footer>
  );
}
Modal.Foot = Foot;

interface DialogCrownProps {
  /** Pip color. `warn` = soft duplicate, `danger` = hard block. */
  tone?: ModalTone;
  /** Mono uppercase pip row text (e.g. "Pozor — možný duplikát"). */
  eyebrow: ReactNode;
  /** Right-side mono detail (e.g. "3 podobné záznamy"). */
  eyebrowMeta?: ReactNode;
  /** Display title (Fraunces serif). Wrap italics in `<em>`. */
  title: ReactNode;
  /** Sub-paragraph. */
  sub?: ReactNode;
  /** Hide the close affordance (rare). */
  hideClose?: boolean;
  className?: string;
}

const PIP_TONE: Record<ModalTone, string> = {
  neutral: 'bg-ink',
  warn: 'bg-warn',
  danger: 'bg-red',
};

export function DialogCrown({
  tone = 'warn',
  eyebrow,
  eyebrowMeta,
  title,
  sub,
  hideClose = false,
  className = '',
}: DialogCrownProps) {
  return (
    <header
      className={
        'relative border-b border-line px-8 pb-[22px] pt-7 max-sm:px-5 max-sm:pb-4 max-sm:pt-[18px] ' +
        className
      }
    >
      <div className="mb-3.5 flex items-center gap-2.5 font-mono text-[10.5px] uppercase tracking-[0.08em]">
        <span aria-hidden className={'block size-2 ' + PIP_TONE[tone]} />
        <span className="text-ink-3">{eyebrow}</span>
        {eyebrowMeta && (
          <>
            <span className="text-ink-3">·</span>
            <span className="font-medium tracking-[0.04em] text-ink">
              {eyebrowMeta}
            </span>
          </>
        )}
      </div>
      <Dialog.Title asChild>
        <h2 className="max-w-[600px] font-display text-[36px] font-normal leading-[1] tracking-[-0.02em] text-ink max-sm:text-[24px] [&>em]:font-light [&>em]:italic [&>em]:text-ink-3">
          {title}
        </h2>
      </Dialog.Title>
      {sub && (
        <Dialog.Description asChild>
          <p className="mt-3 max-w-[580px] text-[13.5px] leading-[1.55] text-ink-2">
            {sub}
          </p>
        </Dialog.Description>
      )}
      {!hideClose && (
        <Dialog.Close
          aria-label="Zavřít"
          className="absolute right-[22px] top-[22px] grid size-7 cursor-pointer place-items-center border border-line-2 font-mono text-[16px] leading-none text-ink-3 transition-colors hover:border-ink hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
        >
          ×
        </Dialog.Close>
      )}
    </header>
  );
}

interface DialogDiffProps {
  /** Two columns — left = "Co zadáváš", right = "Nejlepší shoda". */
  left: { heading: ReactNode; rows: { k: ReactNode; v: ReactNode }[] };
  right: { heading: ReactNode; rows: { k: ReactNode; v: ReactNode }[] };
  className?: string;
}

export function DialogDiff({ left, right, className = '' }: DialogDiffProps) {
  return (
    <div
      className={
        'grid grid-cols-2 border-y border-line bg-bg px-8 py-3.5 ' + className
      }
    >
      {[left, right].map((col, i) => (
        <div
          key={i}
          className={
            i === 1 ? 'border-l border-line pl-[18px]' : 'pr-[18px]'
          }
        >
          <div className="mb-1.5 font-mono text-[9.5px] uppercase tracking-[0.1em] text-ink-3">
            {col.heading}
          </div>
          {col.rows.map((row, j) => (
            <div
              key={j}
              className="flex justify-between gap-3 py-[3px] text-[12px]"
            >
              <span className="font-mono text-[11px] text-ink-3">{row.k}</span>
              <span className="text-right font-mono text-[11px] text-ink">
                {row.v}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

interface HardBlockProps {
  children: ReactNode;
  /** Glyph rendered in the leading square. Defaults to `!`. */
  glyph?: ReactNode;
  className?: string;
}

export function HardBlock({
  children,
  glyph = '!',
  className = '',
}: HardBlockProps) {
  return (
    <div
      className={
        'grid grid-cols-[36px_1fr] items-start gap-3.5 border-b border-line bg-accent-soft px-8 py-[22px] ' +
        className
      }
    >
      <div className="grid size-8 place-items-center bg-accent-fill font-mono text-[16px] font-bold text-accent-ink">
        {glyph}
      </div>
      <div className="text-[13.5px] leading-[1.5] text-ink-2 [&_b]:font-medium [&_b]:text-ink">
        {children}
      </div>
    </div>
  );
}
